<template>
  <!-- POC Cuidados Paliativos: registro manual de atividade educativa (BPA consolidado).
       Usado na tela de Produção SUS. -->
  <div class="default-modal">
    <div
      class="modal"
      :class="{ 'is-active': open }"
    >
      <div
        class="modal-background"
        @click="close"
      />
      <div
        class="modal-card ea-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ea-modal-title"
      >
        <header class="modal-card-head">
          <p
            id="ea-modal-title"
            class="modal-card-title"
          >
            Registrar atividade educativa
          </p>
        </header>

        <section class="modal-card-body">
          <fieldset class="ea-block">
            <legend class="ea-label">
              Tipo de atividade
            </legend>
            <div class="ea-options">
              <label
                v-for="code in codes"
                :key="code"
                class="ea-option"
                :class="{ 'is-selected': form.code === code }"
              >
                <input
                  v-model="form.code"
                  type="radio"
                  class="ea-visually-hidden"
                  name="ea-code"
                  :value="code"
                >
                <span class="ea-option-name">{{ shortName(code) }}</span>
                <span class="ea-option-code">{{ formatProcedureCode(code) }}</span>
              </label>
            </div>
            <p
              v-if="showErrors && errors.code"
              class="ea-error"
            >
              {{ errors.code }}
            </p>
          </fieldset>

          <div class="ea-grid">
            <div class="ea-block">
              <label
                class="ea-label"
                for="ea-date"
              >Data</label>
              <input
                id="ea-date"
                v-model="form.date"
                class="input"
                type="date"
                :min="minDate"
                :max="maxDate"
              >
              <p
                v-if="showErrors && errors.date"
                class="ea-error"
              >
                {{ errors.date }}
              </p>
            </div>

            <div class="ea-block">
              <label
                class="ea-label"
                for="ea-participants"
              >Participantes</label>
              <input
                id="ea-participants"
                v-model.number="form.participants"
                class="input"
                type="number"
                min="1"
              >
              <p
                v-if="showErrors && errors.participants"
                class="ea-error"
              >
                {{ errors.participants }}
              </p>
              <p
                v-else-if="familyGroupBelowRecommended"
                class="ea-hint"
              >
                Para grupo de familiares, a portaria recomenda pelo menos {{ familyGroupMin }} participantes.
              </p>
            </div>
          </div>

          <div class="ea-block">
            <label
              class="ea-label"
              for="ea-description"
            >Descrição</label>
            <input
              id="ea-description"
              v-model.trim="form.description"
              class="input"
              maxlength="120"
              placeholder="Ex.: Roda de conversa sobre controle da dor com a equipe do Hospital São Lucas"
            >
            <p
              v-if="showErrors && errors.description"
              class="ea-error"
            >
              {{ errors.description }}
            </p>
          </div>

          <div class="ea-grid">
            <div class="ea-block">
              <label
                class="ea-label"
                for="ea-professional"
              >Profissional responsável</label>
              <input
                id="ea-professional"
                v-model.trim="form.professionalName"
                class="input"
              >
              <p
                v-if="showErrors && errors.professionalName"
                class="ea-error"
              >
                {{ errors.professionalName }}
              </p>
            </div>

            <div class="ea-block">
              <label
                class="ea-label"
                for="ea-cbo"
              >CBO do responsável</label>
              <div class="select is-fullwidth">
                <select
                  id="ea-cbo"
                  v-model="form.cbo"
                >
                  <option value="">
                    Selecione
                  </option>
                  <option
                    v-for="(label, code) in cboLabels"
                    :key="code"
                    :value="code"
                  >
                    {{ code }} · {{ label }}
                  </option>
                </select>
              </div>
              <p
                v-if="showErrors && errors.cbo"
                class="ea-error"
              >
                {{ errors.cbo }}
              </p>
            </div>
          </div>

          <p class="ea-hint">
            Cada atividade conta como um procedimento no mês, no BPA consolidado, e entra já confirmada.
          </p>
        </section>

        <footer class="modal-card-foot">
          <lila-button
            type="button"
            title="Registrar"
            :is-centralized="true"
            :second-button="{ type: 'button', title: 'Cancelar' }"
            @custom-click-event="save"
            @custom-click-cancel-event="close"
          />
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import Button from "@components/shared/button/Button.vue";
import {
  CBO_LABELS,
  EDUCATIONAL_PROCEDURES,
  FAMILY_GROUP_CODE,
  FAMILY_GROUP_MIN_PARTICIPANTS,
  PROCEDURES,
  formatProcedureCode
} from "@/services/palliative/susProductionApi";

export default {
  name: "EducationalActivityModal",
  components: {
    "lila-button": Button
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    // Sugestão para o responsável: o profissional logado.
    defaultProfessionalName: {
      type: String,
      default: ""
    },
    defaultCbo: {
      type: String,
      default: ""
    }
  },
  emits: ["update:open", "save"],
  data() {
    return {
      form: this.emptyForm(),
      showErrors: false,
      codes: EDUCATIONAL_PROCEDURES,
      cboLabels: CBO_LABELS,
      familyGroupMin: FAMILY_GROUP_MIN_PARTICIPANTS
    };
  },
  computed: {
    // Só a competência atual: a produção é exportada por mês.
    minDate() {
      return moment().startOf("month").format("YYYY-MM-DD");
    },
    maxDate() {
      return moment().format("YYYY-MM-DD");
    },
    familyGroupBelowRecommended() {
      return this.form.code === FAMILY_GROUP_CODE
        && Number(this.form.participants) > 0
        && Number(this.form.participants) < FAMILY_GROUP_MIN_PARTICIPANTS;
    },
    errors() {
      const errors = {};
      if (!this.form.code) errors.code = "Escolha o tipo de atividade.";
      if (!this.form.date) {
        errors.date = "Informe a data.";
      } else if (this.form.date < this.minDate || this.form.date > this.maxDate) {
        errors.date = "A data precisa estar no mês atual e não pode ser futura.";
      }
      const participants = Number(this.form.participants);
      if (!Number.isInteger(participants) || participants < 1) {
        errors.participants = "Informe o número de participantes.";
      }
      if (!this.form.description) errors.description = "Descreva a atividade.";
      if (!this.form.professionalName) errors.professionalName = "Informe o responsável.";
      if (!this.form.cbo) errors.cbo = "Informe o CBO do responsável.";
      return errors;
    }
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.form = this.emptyForm();
        this.showErrors = false;
      }
    }
  },
  methods: {
    emptyForm() {
      return {
        code: "",
        date: moment().format("YYYY-MM-DD"),
        participants: "",
        description: "",
        professionalName: this.defaultProfessionalName,
        cbo: this.defaultCbo
      };
    },
    shortName(code) {
      return PROCEDURES[code].name.replace("Atividade educativa em CP ", "");
    },
    formatProcedureCode,
    close() {
      this.$emit("update:open", false);
    },
    save() {
      this.showErrors = true;
      if (Object.keys(this.errors).length > 0) return;
      this.$emit("save", { ...this.form, participants: Number(this.form.participants) });
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #4f3f82;
$primary-soft: #ece9f5;
$muted: #6b6b7b;
$border: #dcd8e6;
$critical: #c0262d;

.ea-modal {
  max-width: 620px;
  width: calc(100% - 32px);
}

.ea-block {
  margin-bottom: 16px;
}

.ea-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.ea-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #484848;
}

.ea-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.ea-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid $border;
  border-radius: 10px;
  cursor: pointer;

  &.is-selected {
    border-color: $primary;
    background-color: $primary-soft;
  }

  &:focus-within {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }
}

.ea-option-name {
  font-size: 13px;
  font-weight: 600;
  color: #2b2b3a;
}

.ea-option-code {
  font-size: 12px;
  color: $muted;
}

.ea-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.ea-hint {
  margin-top: 6px;
  font-size: 12px;
  color: $muted;
}

.ea-error {
  margin-top: 6px;
  font-size: 12px;
  color: $critical;
}

@media (max-width: 640px) {
  .ea-grid,
  .ea-options {
    grid-template-columns: 1fr;
  }
}
</style>
